import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function TourismHeroSection() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="tourism-hero"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{ background: '#0A0A0A' }}
    >
      {/* Background layers */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 90% 70% at 50% 40%, #0e1a10 0%, #0A0A0A 55%, #060606 100%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
            backgroundRepeat: 'repeat',
            backgroundSize: '256px 256px',
          }}
        />
      </div>
      <div className="absolute inset-0 hero-grid opacity-25 pointer-events-none" />
      <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-ds-red/7 blur-[150px] rounded-full pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-green-900/15 blur-[120px] rounded-full pointer-events-none" />

      {/* Hero image strip on the right */}
      <div className="absolute right-0 top-0 bottom-0 w-2/5 overflow-hidden hidden xl:block">
        <img
          src="https://public.readdy.ai/ai/img_res/417e9b59769c3c4ebc1de5a08f200379.jpg"
          alt="Queensland tourism"
          className="w-full h-full object-cover object-center opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-transparent to-[#0A0A0A]/40" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-28 pb-20">
        <div className="max-w-3xl">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 border border-ds-red/40 px-4 py-2 mb-8 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ background: 'rgba(180,255,57,0.07)' }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-compass-3-line text-ds-red text-xs" />
            </div>
            <span className="font-body text-ds-red text-xs tracking-[0.3em] uppercase">
              Tourism &amp; Hospitality Website Specialists
            </span>
          </div>

          {/* H1 — primary SEO keyword */}
          <h1
            className={`font-display text-white leading-none mb-6 transition-all duration-1000 delay-150 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ fontSize: 'clamp(2.8rem, 7vw, 7.5rem)', lineHeight: '0.9', letterSpacing: '0.03em' }}
          >
            TOURISM WEBSITE
            <br />
            DESIGN &amp; SEO FOR
            <br />
            <span className="text-ds-red neon-text-red">AUSTRALIAN TOUR OPERATORS.</span>
          </h1>

          {/* Sub */}
          <p
            className={`font-body text-white/65 text-lg md:text-xl max-w-2xl mt-8 mb-6 leading-relaxed transition-all duration-1000 delay-300 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            We design and build websites for Australian tourism operators — reef trips, dive centres, ferry services, adventure charters, and accommodation — that <strong className="text-white">rank on Google, convert travellers, and get cited by AI</strong> when someone asks ChatGPT who to book.
          </p>

          {/* Speciality tags */}
          <div
            className={`flex flex-wrap gap-2 mb-10 transition-all duration-1000 delay-400 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {['Reef & Dive', 'Ferry Services', 'Adventure Charters', 'Accommodation', 'Wildlife Tours', 'Eco Tourism'].map((tag) => (
              <span key={tag} className="font-body text-white/50 text-xs border border-ds-border px-3 py-1.5">
                {tag}
              </span>
            ))}
          </div>

          {/* CTAs */}
          <div
            className={`flex flex-col sm:flex-row gap-4 transition-all duration-1000 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <button
              onClick={() => scrollTo('tourism-contact')}
              className="btn-red gap-2 text-sm"
            >
              <span>Get a Free Tourism Website Consultation</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </button>
            <button
              onClick={() => scrollTo('tourism-work')}
              className="btn-outline text-sm whitespace-nowrap"
            >
              See Our Tourism Work
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div
          className={`mt-20 max-w-3xl transition-all duration-1000 delay-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="h-px bg-gradient-to-r from-ds-red via-ds-red/50 to-transparent" />
          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-ds-border/60">
            {[
              { num: '3', label: 'Tourism Clients Ranked #1' },
              { num: '52', suffix: ' days', label: 'Fastest #1 Ranking' },
              { num: 'AI', label: 'ChatGPT & Gemini Cited' },
              { num: '100%', label: 'Organic — Zero Ad Spend' },
            ].map((s) => (
              <div key={s.label} className="px-6 py-7 text-center md:text-left">
                <div className="font-display text-4xl md:text-5xl text-ds-red leading-none mb-1" style={{ letterSpacing: '0.03em' }}>
                  {s.num}
                  {s.suffix && <span className="text-2xl">{s.suffix}</span>}
                </div>
                <div className="font-body text-white/50 text-xs tracking-[0.15em] uppercase leading-snug">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="h-px bg-ds-border/40" />
        </div>

        {/* GEO callout */}
        <div
          className={`mt-4 max-w-3xl transition-all duration-1000 delay-800 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <Link
            to="/geo-aeo"
            className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 w-full border border-ds-red/30 bg-ds-red/5 px-5 py-3.5 hover:bg-ds-red/10 transition-all duration-300"
            style={{ boxShadow: '0 0 24px rgba(180,255,57,0.08)' }}
          >
            <div className="flex items-center gap-3">
              <span className="relative flex h-2 w-2 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ds-red opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-ds-red" />
              </span>
              <span className="font-body text-white/70 text-xs leading-tight">
                A traveller just asked ChatGPT for reef trip recommendations. <strong className="text-white">Did it mention your business?</strong>
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0 text-ds-red group-hover:gap-2.5 transition-all duration-300">
              <span className="font-display text-xs tracking-widest uppercase whitespace-nowrap">Learn About GEO / AEO</span>
              <i className="ri-arrow-right-line text-xs" />
            </div>
          </Link>
        </div>
      </div>

      {/* Scroll cue */}
      <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-1000 delay-1000 ${loaded ? 'opacity-50' : 'opacity-0'}`}>
        <span className="font-body text-white/40 text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/30 to-transparent" />
      </div>
    </section>
  );
}
